import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, Check, ShieldCheck, Undo2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import ProjectHeader from "../components/ProjectHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import ConfirmDialog from "../components/ConfirmDialog";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";

const INT_EXT = ["INT", "EXT", "INT/EXT"];
const TOD = ["DAY", "NIGHT"];

function SceneRow({ scene, onChange, onDelete }) {
    return (
        <li
            data-testid={`draft-scene-${scene.id}`}
            className="rounded-xl border border-white/10 bg-card p-4 space-y-3"
        >
            <div className="grid grid-cols-4 gap-2">
                <Input
                    data-testid={`draft-number-${scene.id}`}
                    value={scene.scene_number || ""}
                    onChange={(e) => onChange({ ...scene, scene_number: e.target.value })}
                    placeholder="#"
                    className="h-11 col-span-1 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary text-center mono"
                />
                <Input
                    data-testid={`draft-heading-${scene.id}`}
                    value={scene.heading || ""}
                    onChange={(e) => onChange({ ...scene, heading: e.target.value })}
                    placeholder="INT. HOUSE – DAY"
                    className="h-11 col-span-3 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                />
            </div>

            <div className="flex gap-2 flex-wrap">
                {INT_EXT.map((v) => {
                    const active = scene.int_ext === v;
                    return (
                        <button
                            key={v}
                            type="button"
                            data-testid={`draft-intext-${scene.id}-${v.toLowerCase().replace("/", "-")}`}
                            onClick={() => onChange({ ...scene, int_ext: active ? null : v })}
                            className={`tap min-tap px-3 rounded-full text-xs border transition-colors ${
                                active
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-secondary text-neutral-300 border-white/10 hover:bg-white/10"
                            }`}
                        >
                            {v}
                        </button>
                    );
                })}
                {TOD.map((v) => {
                    const active = scene.time_of_day === v;
                    return (
                        <button
                            key={v}
                            type="button"
                            data-testid={`draft-tod-${scene.id}-${v.toLowerCase()}`}
                            onClick={() => onChange({ ...scene, time_of_day: active ? null : v })}
                            className={`tap min-tap px-3 rounded-full text-xs border transition-colors ${
                                active
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-secondary text-neutral-300 border-white/10 hover:bg-white/10"
                            }`}
                        >
                            {v}
                        </button>
                    );
                })}
                <div className="ml-auto">
                    <button
                        type="button"
                        data-testid={`draft-delete-${scene.id}`}
                        onClick={() => onDelete(scene)}
                        title="Remove this scene from the proposed list (it has not been confirmed yet)"
                        className="tap min-tap px-3 rounded-full text-xs border border-red-500/30 text-red-300 bg-red-500/10 hover:bg-red-500/20 inline-flex items-center gap-1"
                    >
                        <Trash2 size={12} /> Remove from list
                    </button>
                </div>
            </div>
        </li>
    );
}

export default function ScenesReview() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();

    const [project, setProject] = useState(null);
    const [scenes, setScenes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [error, setError] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [discardOpen, setDiscardOpen] = useState(false);
    const [discarding, setDiscarding] = useState(false);

    const [newHeading, setNewHeading] = useState("");
    const [newNumber, setNewNumber] = useState("");
    const [adding, setAdding] = useState(false);

    const load = async () => {
        const [{ data: p }, { data: s }] = await Promise.all([
            api.get(`${apiBase}/${projectId}`),
            api.get(`${apiBase}/${projectId}/scenes`, { params: { status: "draft" } }),
        ]);
        setProject(p);
        setScenes(s);
    };

    useEffect(() => {
        (async () => {
            try {
                await load();
            } catch (e) {
                setError(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const updateScene = (updated) => {
        setScenes((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        setSaving((m) => ({ ...m, [updated.id]: true }));
        // debounce-ish: fire immediately, keep simple for MVP
        api
            .patch(`${apiBase}/${projectId}/scenes/${updated.id}`, {
                heading: updated.heading,
                scene_number: updated.scene_number || null,
                int_ext: updated.int_ext,
                time_of_day: updated.time_of_day,
            })
            .catch((err) => setError(formatApiError(err)))
            .finally(() => setSaving((m) => ({ ...m, [updated.id]: false })));
    };

    const deleteScene = async (scene) => {
        try {
            await api.delete(`${apiBase}/${projectId}/scenes/${scene.id}`);
            setScenes((prev) => prev.filter((s) => s.id !== scene.id));
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    const addScene = async (e) => {
        e.preventDefault();
        if (!newHeading.trim()) return;
        setAdding(true);
        try {
            const { data } = await api.post(`${apiBase}/${projectId}/scenes`, {
                heading: newHeading.trim(),
                scene_number: newNumber.trim() || null,
            });
            // new scene may be created as draft (since no confirmed exist in this flow)
            setScenes((prev) => [...prev, data].filter((s) => s.status === "draft"));
            setNewHeading("");
            setNewNumber("");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setAdding(false);
        }
    };

    const confirm = async () => {
        setConfirming(true);
        setError("");
        try {
            await api.post(`${apiBase}/${projectId}/scenes/confirm`);
            navigate(`${routeBase}/${projectId}/onset`, { replace: true });
        } catch (err) {
            setError(formatApiError(err));
            setConfirming(false);
        }
    };

    const discard = async () => {
        setDiscardOpen(false);
        setDiscarding(true);
        try {
            await api.post(`${apiBase}/${projectId}/scenes/reset-draft`);
            navigate(`${routeBase}/${projectId}/prep`, { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setDiscarding(false);
        }
    };

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    const canManage = isOwner || isAdmin;

    return (
        <div data-testid="scenes-review-page" className="min-h-screen">
            <ProjectHeader projectName={project?.name} subtitle="Scene review" />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-48">
                <div className="mb-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                        Review
                    </div>
                    <h1 className="font-display text-3xl font-black text-white mt-2">
                        Confirm your scene list
                    </h1>
                    <p className="text-sm text-neutral-400 mt-2">
                        Edits save automatically. Nothing reaches On Set until you hit
                        <span className="text-white font-medium"> Confirm</span>.
                    </p>
                </div>

                {error ? (
                    <p data-testid="review-error" className="text-sm text-red-400 mb-4">
                        {error}
                    </p>
                ) : null}

                {loading ? (
                    <div className="space-y-3" data-testid="review-loading">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="h-28 rounded-xl border border-white/10 bg-card animate-pulse"
                            />
                        ))}
                    </div>
                ) : scenes.length === 0 ? (
                    <div
                        data-testid="review-empty"
                        className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                    >
                        <p className="text-sm text-neutral-400">
                            No draft scenes. Build Scenes & Cast from a script in Fittings, or add one below.
                        </p>
                    </div>
                ) : (
                    <ul data-testid="review-list" className="space-y-3 mb-6">
                        {scenes.map((s) => (
                            <SceneRow
                                key={s.id}
                                scene={s}
                                onChange={updateScene}
                                onDelete={deleteScene}
                            />
                        ))}
                    </ul>
                )}

                <form
                    onSubmit={addScene}
                    className="rounded-xl border border-white/10 bg-card p-4 space-y-3 mb-6"
                >
                    <div className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                        Add a scene
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                        <Input
                            data-testid="review-new-number"
                            value={newNumber}
                            onChange={(e) => setNewNumber(e.target.value)}
                            placeholder="#"
                            className="h-11 col-span-1 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary text-center mono"
                        />
                        <Input
                            data-testid="review-new-heading"
                            value={newHeading}
                            onChange={(e) => setNewHeading(e.target.value)}
                            placeholder="INT. HOUSE – DAY"
                            className="h-11 col-span-3 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                    </div>
                    <Button
                        type="submit"
                        data-testid="review-add-btn"
                        disabled={adding || !newHeading.trim()}
                        className="w-full min-tap"
                        variant="secondary"
                    >
                        <Plus size={16} className="mr-2" /> Add scene
                    </Button>
                </form>

                <div className="sticky bottom-4 z-40">
                    <div className="rounded-xl border border-white/10 bg-background/80 backdrop-blur-xl p-3 flex items-center gap-2">
                        <Button
                            data-testid="review-discard-btn"
                            type="button"
                            onClick={() => setDiscardOpen(true)}
                            variant="secondary"
                            className="min-tap"
                        >
                            <Undo2 size={16} className="mr-2" />
                            Discard
                        </Button>
                        <Button
                            data-testid="review-confirm-btn"
                            type="button"
                            onClick={confirm}
                            disabled={confirming || scenes.length === 0 || !canManage}
                            className="flex-1 min-tap font-semibold tracking-wide"
                            title={!canManage ? "Only the project owner or administrator can confirm scenes" : ""}
                        >
                            <ShieldCheck size={16} className="mr-2" />
                            {confirming
                                ? "Confirming…"
                                : canManage
                                ? `Confirm scenes (${scenes.length})`
                                : "Only owner or admin can confirm"}
                        </Button>
                    </div>
                    {Object.values(saving).some(Boolean) ? (
                        <div
                            data-testid="review-saving-indicator"
                            className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 text-center mt-2 flex items-center justify-center gap-1"
                        >
                            <Check size={10} /> Saving edits
                        </div>
                    ) : null}
                </div>
            </main>

            <ConfirmDialog
                open={discardOpen}
                onOpenChange={setDiscardOpen}
                title="Discard draft scenes?"
                description={"All draft scenes from the latest script process will be removed.\n\nThis cannot be undone. The original script file stays untouched — you can process it again any time."}
                confirmLabel="Discard drafts"
                cancelLabel="Keep drafts"
                onConfirm={discard}
                busy={discarding}
                tone="danger"
                testidPrefix="confirm-discard-drafts"
            />
        </div>
    );
}
